import useReveal from "../hooks/useReveal";
import CTA from "../components/CTA";
import { Sparkles } from "lucide-react";
import { founderImage, teamImage, brand } from "../data/mock";

export default function About() {
  useReveal();
  return (
    <div className="pt-32">
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7 reveal">
          <div className="kicker">About</div>
          <h1 className="font-display text-4xl md:text-7xl leading-[1.02] mt-6">
            Celebrations planned with <span className="italic">heart</span>, decor designed with <span className="italic">care</span>.
          </h1>
        </div>
        <div className="md:col-span-5 reveal">
          <p className="text-[hsl(var(--ink-soft))] leading-relaxed">
            {brand.name} is an atelier of weddings and events founded on heritage, warmth and craft.
            From intimate ceremonies to headline celebrations, every experience is built around the
            people at its heart.
          </p>
        </div>
      </section>

      <section className="reveal img-zoom">
        <img src={founderImage} alt="Shubhra" className="w-full h-auto" />
      </section>

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5 reveal">
          <div className="kicker">Meet Shubhra</div>
          <h2 className="font-display text-3xl md:text-5xl mt-6 leading-tight">
            Six years of <span className="italic">quiet obsession</span> with the details that make a day unforgettable.
          </h2>
        </div>
        <div className="md:col-span-7 space-y-6 text-[hsl(var(--ink-soft))] leading-relaxed reveal">
          <p>
            Born from a love of storytelling and Indian traditions, Shobha with Shubhra began with a
            simple promise — to design celebrations that feel alive, not staged. Every mandap, tablescape
            and stage is built to hold memory.
          </p>
          <p>
            We work with a small circle of trusted florists, artisans, chefs, entertainers and photographers,
            hand-picked over six years, so every element of your day is carried by people who care as much as we do.
          </p>
          <p>
            Based in the Midwest, we produce weddings and events — wherever a celebration calls.
          </p>
          <div className="pt-4 flex flex-wrap items-center gap-4">
            <CTA to="/contact" label="Work with us" />
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("sws:open-mood-board"))}
              data-testid="about-mood-board-cta"
              className="inline-flex items-center gap-2 border border-[hsl(var(--ink))] text-[hsl(var(--ink))] px-6 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--bg))] transition-colors duration-500"
            >
              <Sparkles size={14} /> Try Vision Studio
            </button>
          </div>
          <p className="mt-4 text-xs md:text-sm text-[hsl(var(--ink-soft))] italic max-w-lg">
            Not sure where to begin? Describe your vibe and Ayaan will sketch a concept board
            in seconds — reviewed by Shubhra, always.
          </p>
        </div>
      </section>

      {/* Family-owned & community give-back */}
      <section className="bg-[hsl(var(--bg-alt))]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-6 reveal">
            <div className="kicker">A family-owned atelier</div>
            <h3 className="font-display text-3xl md:text-5xl mt-6 leading-[1.1]">
              Built by <span className="italic">one family</span>, in service of many.
            </h3>
          </div>
          <div className="md:col-span-6 space-y-6 text-[hsl(var(--ink-soft))] leading-relaxed reveal">
            <p>
              Shobha with Shubhra is a proud family-owned business. Every enquiry, every walkthrough,
              every late-night floral revision is handled by us personally — not passed to a call
              centre. When you hire us you get our name, our judgement and our hands on your day.
            </p>
            <p>
              We also believe that celebrations should give back. <strong>10% of every
              project&rsquo;s profits</strong> is directed to community service initiatives we
              love — supporting local food pantries, women&rsquo;s shelters, and cultural
              programmes across Wisconsin. When your day ends, its generosity keeps going.
            </p>
            <p className="text-xs tracking-[0.25em] uppercase text-[hsl(var(--accent))]">
              Family-run · Locally accountable · Community-first
            </p>
          </div>
        </div>
      </section>

      <section className="reveal img-zoom">
        <img src={teamImage} alt="Team" className="w-full h-[60vh] object-cover" />
      </section>

      <section className="mx-auto max-w-[1200px] px-6 md:px-10 py-24 text-center">
        <div className="kicker">Our values</div>
        <div className="mt-12 grid md:grid-cols-3 gap-12">
          {[
            { t: "Craft", d: "We favour texture, patina and hand-made details over generic perfection." },
            { t: "Care", d: "Every client is a friendship. We answer emails at 11pm because we can\u2019t help ourselves." },
            { t: "Community", d: "10% of every project supports Wisconsin food pantries, women\u2019s shelters and cultural programmes." },
          ].map((v) => (
            <div key={v.t} className="reveal">
              <div className="font-display text-3xl md:text-4xl italic">{v.t}</div>
              <div className="hairline my-6 mx-auto w-16" />
              <p className="text-[hsl(var(--ink-soft))] leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
